import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Wire-format types - match the backend response shape from
// stock_screener.web.admin_runtime_config_routes._effective_to_dict.
export interface EffectiveConfig {
  key: string;
  tier: 1 | 2 | 3 | 4;
  value_type: 'bool' | 'int' | 'float' | 'str';
  default: string;
  section: string;
  label_key: string;
  description_key: string;
  hot_apply: boolean;
  restart_required: boolean;
  min: number | null;
  max: number | null;
  enum: string[] | null;
  write_blocked: boolean;
  value: string;
  source: 'env' | 'admin' | 'default';
  updated_by: string;
  updated_at: string | null;
  version: number;
}

export interface AuditEntry {
  id: number;
  key: string;
  old_value: string;
  new_value: string;
  changed_by: string;
  via: string;
  changed_at: string;
  reason_text: string;
  ip_address: string;
}

interface RuntimeConfigState {
  items: Record<string, EffectiveConfig>;
  tierFourDenylist: string[];
  loading: boolean;
  error: string | null;
  // Code-review fix: also capture the prior `source` so failEdit's
  // rollback path can restore it. beginEdit force-sets source to 'admin'
  // for the optimistic display; without the prior, a rolled-back failed
  // edit would leave the SourceBadge stuck on 'admin' even though the
  // DB row never changed.
  pendingKeys: Record<string, { previousValue: string; previousVersion: number; previousSource: EffectiveConfig['source'] }>;
  audit: AuditEntry[];
  auditLoading: boolean;
  conflictMessage: string | null;
}

const initialState: RuntimeConfigState = {
  items: {},
  tierFourDenylist: [],
  loading: false,
  error: null,
  pendingKeys: {},
  audit: [],
  auditLoading: false,
  conflictMessage: null,
};

const runtimeConfigSlice = createSlice({
  name: 'runtimeConfig',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setItems(
      state,
      action: PayloadAction<{ items: EffectiveConfig[]; denylist: string[] }>,
    ) {
      const next: Record<string, EffectiveConfig> = {};
      for (const item of action.payload.items) {
        next[item.key] = item;
      }
      state.items = next;
      state.tierFourDenylist = action.payload.denylist || [];
    },
    upsertItem(state, action: PayloadAction<EffectiveConfig>) {
      state.items[action.payload.key] = action.payload;
    },
    // Optimistic edit lifecycle. beginEdit captures the prior value so a
    // subsequent failEdit can roll back. completeEdit clears the pending
    // marker; failEdit restores the captured prior + surfaces a message.
    beginEdit(
      state,
      action: PayloadAction<{ key: string; nextValue: string }>,
    ) {
      const current = state.items[action.payload.key];
      if (!current) return;
      state.pendingKeys[action.payload.key] = {
        previousValue: current.value,
        previousVersion: current.version,
        previousSource: current.source,
      };
      state.items[action.payload.key] = {
        ...current,
        value: action.payload.nextValue,
        source: 'admin',
      };
      state.conflictMessage = null;
    },
    completeEdit(state, action: PayloadAction<EffectiveConfig>) {
      state.items[action.payload.key] = action.payload;
      delete state.pendingKeys[action.payload.key];
    },
    failEdit(
      state,
      action: PayloadAction<{ key: string; message?: string; current?: EffectiveConfig | null }>,
    ) {
      const pending = state.pendingKeys[action.payload.key];
      if (action.payload.current) {
        // Server returned the freshly-fetched current row (e.g. on 409).
        state.items[action.payload.key] = action.payload.current;
      } else if (pending) {
        const prior = state.items[action.payload.key];
        if (prior) {
          state.items[action.payload.key] = {
            ...prior,
            value: pending.previousValue,
            version: pending.previousVersion,
            // Code-review fix: also restore source so the SourceBadge
            // does not stay stuck on 'admin' after a failed optimistic
            // edit (beginEdit force-set it to 'admin' for display).
            source: pending.previousSource,
          };
        }
      }
      delete state.pendingKeys[action.payload.key];
      if (action.payload.message) {
        state.conflictMessage = action.payload.message;
      }
    },
    clearConflict(state) {
      state.conflictMessage = null;
    },
    setAuditLoading(state, action: PayloadAction<boolean>) {
      state.auditLoading = action.payload;
    },
    setAudit(state, action: PayloadAction<AuditEntry[]>) {
      state.audit = action.payload;
    },
  },
});

export const {
  setLoading,
  setError,
  setItems,
  upsertItem,
  beginEdit,
  completeEdit,
  failEdit,
  clearConflict,
  setAuditLoading,
  setAudit,
} = runtimeConfigSlice.actions;

export default runtimeConfigSlice.reducer;
